import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { LOGO_URL, COMPANY_NAME } from '@/config/brand';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Lock, AlertCircle, CheckCircle2, ArrowLeft } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [token, setToken] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const t = searchParams.get('token');
    if (!t || t.length < 20) {
      setError('Link non valido o incompleto. Richiedi un nuovo link di reset.');
    } else {
      setToken(t);
    }
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!token) {
      setError('Token mancante. Richiedi un nuovo link di reset.');
      return;
    }
    if (password.length < 8) {
      setError('La password deve avere almeno 8 caratteri.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Le password non coincidono.');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/password-reset/confirm`, {
        token,
        new_password: password,
      });
      setSuccess(true);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Impossibile aggiornare la password. Il link potrebbe essere scaduto.');
    } finally {
      setLoading(false);
    }
  };

  // Auto-redirect al login dopo 3s se il reset è andato a buon fine (cleanup on unmount)
  useEffect(() => {
    if (!success) return undefined;
    const t = setTimeout(() => navigate('/login', { replace: true }), 3000);
    return () => clearTimeout(t);
  }, [success, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-slate-50">
      <div className="w-full max-w-md">
        <div className="flex flex-col items-center text-center mb-8">
          <div className="bg-white rounded-lg p-4 shadow-sm border border-slate-200 mb-4">
            <img src={LOGO_URL} alt="CR" className="h-28 w-auto object-contain" />
          </div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900 mb-2">{COMPANY_NAME}</h1>
        </div>

        <Card className="border-slate-200 shadow-none">
          <CardHeader className="space-y-1 pb-4">
            <CardTitle className="text-2xl font-bold tracking-tight">Reimposta password</CardTitle>
            <CardDescription className="text-slate-500">
              {success ? 'Password aggiornata con successo' : 'Inserisci una nuova password per il tuo account'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {success ? (
              <div className="space-y-4">
                <div className="flex items-start gap-3 p-4 text-sm text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-md" data-testid="reset-password-success">
                  <CheckCircle2 className="h-5 w-5 flex-shrink-0 mt-0.5 text-emerald-600" />
                  <div>
                    <div className="font-semibold mb-1">Password aggiornata!</div>
                    <div>Sarai reindirizzato alla pagina di accesso tra 3 secondi.</div>
                  </div>
                </div>
                <Button
                  type="button"
                  className="w-full bg-slate-900 text-white hover:bg-slate-800 font-medium"
                  onClick={() => navigate('/login', { replace: true })}
                  data-testid="reset-password-goto-login-btn"
                >
                  Vai al login
                </Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                {error && (
                  <div className="flex items-start gap-2 p-3 text-sm text-red-800 bg-red-100 border border-red-200 rounded-md" data-testid="reset-password-error">
                    <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                    <span>{error}</span>
                  </div>
                )}
                <div className="space-y-2">
                  <Label htmlFor="new-password" className="text-slate-700">Nuova password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      id="new-password"
                      type="password"
                      autoComplete="new-password"
                      placeholder="Almeno 8 caratteri"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="pl-10 border-slate-200"
                      required
                      minLength={8}
                      disabled={!token || loading}
                      data-testid="reset-password-new-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirm-password" className="text-slate-700">Conferma password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      id="confirm-password"
                      type="password"
                      autoComplete="new-password"
                      placeholder="Ripeti la nuova password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="pl-10 border-slate-200"
                      required
                      minLength={8}
                      disabled={!token || loading}
                      data-testid="reset-password-confirm-input"
                    />
                  </div>
                </div>
                <Button
                  type="submit"
                  className="w-full bg-slate-900 text-white hover:bg-slate-800 font-medium"
                  disabled={loading || !token}
                  data-testid="reset-password-submit-btn"
                >
                  {loading ? 'Aggiornamento...' : 'Reimposta password'}
                </Button>
                <div className="text-center pt-2">
                  <Link
                    to="/login"
                    className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900"
                    data-testid="reset-password-back-to-login"
                  >
                    <ArrowLeft className="h-3.5 w-3.5" />
                    Torna al login
                  </Link>
                </div>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
